// Small shared pieces. The design expresses these as repeated inline styles;
// they're extracted here so a tweak lands in one place.

import type { CSSProperties, ReactNode } from "react";
import type { Signal } from "../data/types";

const muted = (pct: number): string => `color-mix(in srgb, var(--color-text) ${pct}%, transparent)`;

export function Chip({
  children, on, onClick, style, title,
}: {
  children: ReactNode; on?: boolean; onClick?: (e: React.MouseEvent) => void;
  style?: CSSProperties; title?: string;
}) {
  return (
    <button className="k-chip" data-on={on ? "true" : "false"} onClick={onClick} style={style} title={title}
      aria-pressed={on}>
      {children}
    </button>
  );
}

export function SectionLabel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <h6 style={{ margin: "0 0 7px", color: muted(55), ...style }}>{children}</h6>;
}

/**
 * A signal's contribution as a proportional bar.
 *
 * Two flex children rather than a percentage width so the fill and the
 * remainder always add up exactly, with no sub-pixel rounding gap at 100%.
 * `Math.max(2, …)` keeps a zero-valued signal visible as a sliver — a signal
 * that fired and scored nothing is information, and an empty track reads as
 * "not computed".
 */
export function SignalBar({ signal, height, compact }: { signal: Signal; height: number; compact?: boolean }) {
  const pct = Math.round(signal.value * 100);
  return (
    <div title={signal.note}>
      <div style={{ display: "flex", fontSize: compact ? 10 : 11, color: compact ? muted(55) : undefined }}>
        <span>{signal.label}</span>
        <span style={{ marginLeft: "auto", color: compact ? undefined : muted(55) }}>{pct}%</span>
      </div>
      <div style={{
        display: "flex", height, borderRadius: height / 2 + 0.5, overflow: "hidden",
        background: muted(10), marginTop: compact ? 2 : 4,
      }}>
        <div style={{ flex: Math.max(2, pct), background: "var(--color-accent-500)" }} />
        <div style={{ flex: Math.max(0, 100 - pct) }} />
      </div>
    </div>
  );
}

/** The stand-in cover: initials on a gradient, used until a real cover loads. */
export function CoverArt({
  initials, coverPath, accent, style, fontSize, onClick, badge,
}: {
  initials: string; coverPath?: string; accent?: boolean;
  style?: CSSProperties; fontSize?: number; onClick?: () => void;
  /** Overlay pinned to the cover's top-right — the match % or shelf state. */
  badge?: ReactNode;
}) {
  const gradient = accent
    ? "linear-gradient(155deg, var(--color-accent-800), var(--color-neutral-900))"
    : "linear-gradient(155deg, var(--color-neutral-800), var(--color-neutral-900))";
  const ink = accent
    ? "color-mix(in srgb, var(--color-accent-300) 55%, transparent)"
    : "color-mix(in srgb, var(--color-neutral-400) 55%, transparent)";

  return (
    <div onClick={onClick} style={{ position: "relative", background: gradient, display: "grid", placeItems: "center", ...style }}>
      {coverPath ? (
        // Always the proxy path. MangaDex serves a troll image to anyone who
        // hotlinks, so an upstream URL here would render garbage.
        <img src={coverPath} alt="" loading="lazy"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <span style={{
          fontFamily: "var(--font-heading)", fontSize: fontSize ?? 30, fontWeight: 600,
          letterSpacing: ".06em", color: ink,
        }}>{initials}</span>
      )}
      {badge != null && <div style={{ position: "absolute", top: 6, right: 6 }}>{badge}</div>}
    </div>
  );
}

export const mutedColor = muted;
